import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Button, 
    ButtonGroup, 
    FormControl,
    Grid,
    Typography,
    IconButton,
    Popover,
    Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

/**
 * DateFilter component for selecting date ranges with presets and custom date picker
 * 
 * @param {Object} props
 * @param {Object} props.dateRange - Current date range with startDate, endDate and preset
 * @param {Function} props.onDateRangeChange - Callback when date range changes
 * @param {Array} props.presets - Optional custom presets
 */
const DateFilter = ({ 
    dateRange, 
    onDateRangeChange,
    presets = [
        { id: 'today', label: 'Today' },
        { id: 'yesterday', label: 'Yesterday' },
        { id: 'last3Days', label: 'Last 3 Days' },
        { id: 'last7Days', label: 'Last 7 Days' },
        { id: 'last30Days', label: 'Last 30 Days' },
        { id: 'custom', label: 'Custom Range' }
    ]
}) => {
    const [activePreset, setActivePreset] = useState(dateRange.preset || 'last7Days');
    const [startDate, setStartDate] = useState(dateRange.startDate || null);
    const [endDate, setEndDate] = useState(dateRange.endDate || null);
    const [showCustomDatePicker, setShowCustomDatePicker] = useState(activePreset === 'custom');
    const [anchorEl, setAnchorEl] = useState(null);

    // Calculate date range based on preset
    const calculateDateRange = (presetId) => {
        const today = new Date();
        let start = null;
        let end = null;

        switch (presetId) {
            case 'today':
                // For today, both start and end dates should be today
                const todayDate = new Date(today);
                todayDate.setHours(0, 0, 0, 0);
                
                start = new Date(todayDate);
                end = new Date(todayDate);
                end.setHours(23, 59, 59, 999);
                
                console.debug('DateFilter - Setting today date range:', {
                    start: start.toISOString(),
                    end: end.toISOString(),
                    startFormatted: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`,
                    endFormatted: `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`
                });
                break;
                
            case 'yesterday':
                // For yesterday, both start and end dates should be yesterday
                const yesterday = new Date(today);
                yesterday.setDate(today.getDate() - 1);
                yesterday.setHours(0, 0, 0, 0);
                
                start = new Date(yesterday);
                end = new Date(yesterday);
                end.setHours(23, 59, 59, 999);
                
                console.debug('DateFilter - Setting yesterday date range:', {
                    start: start.toISOString(),
                    end: end.toISOString(),
                    startFormatted: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`,
                    endFormatted: `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`
                });
                break;
                
            case 'last3Days':
                start = startOfDay(subDays(today, 2));
                end = endOfDay(today);
                break;
                
            case 'last7Days':
                start = startOfDay(subDays(today, 6));
                end = endOfDay(today);
                break;
                
            case 'last30Days':
                start = startOfDay(subDays(today, 29));
                end = endOfDay(today);
                break;
                
            case 'custom':
                // Keep existing custom dates
                start = startDate;
                end = endDate;
                break;
                
            default:
                start = startOfDay(subDays(today, 6));
                end = endOfDay(today);
        }

        return { startDate: start, endDate: end, preset: presetId };
    };

    // Initialize dates on component mount
    useEffect(() => {
        if (!dateRange.startDate || !dateRange.endDate) {
            const initialRange = calculateDateRange(activePreset);
            setStartDate(initialRange.startDate);
            setEndDate(initialRange.endDate);
            onDateRangeChange(initialRange);
        }
    }, []);

    // Handle preset button click
    const handlePresetClick = (presetId) => {
        setActivePreset(presetId);
        setShowCustomDatePicker(presetId === 'custom');
        
        if (presetId !== 'custom') {
            const newDateRange = calculateDateRange(presetId);
            setStartDate(newDateRange.startDate);
            setEndDate(newDateRange.endDate);
            onDateRangeChange(newDateRange);
        }
        
        // Close popover after selection
        if (presetId !== 'custom') {
            setAnchorEl(null);
        }
    };

    // Handle custom date change
    const handleCustomDateChange = (type, date) => {
        if (type === 'start') {
            setStartDate(date);
            if (date && endDate) {
                onDateRangeChange({
                    startDate: startOfDay(date),
                    endDate: endDate,
                    preset: 'custom'
                });
            }
        } else {
            setEndDate(date);
            if (startDate && date) {
                onDateRangeChange({
                    startDate: startDate,
                    endDate: endOfDay(date),
                    preset: 'custom'
                });
                // Close popover after both dates are selected
                setAnchorEl(null);
            }
        }
    };

    // Format date for display
    const formatDate = (date) => {
        return date ? format(date, 'MMM dd, yyyy') : '';
    };

    // Handle popover open/close
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'date-range-popover' : undefined;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Select Date Range">
                <IconButton 
                    onClick={handleClick} 
                    size="small" 
                    sx={{ 
                        color: 'white',
                        mr: 1,
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                        }
                    }}
                >
                    <CalendarTodayIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 'medium' }}>
                {formatDate(startDate)} - {formatDate(endDate)}
            </Typography>
            
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        p: 2,
                        bgcolor: '#2c3040',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        borderRadius: '8px',
                        width: '320px'
                    }
                }}
            >
                <Typography variant="subtitle2" gutterBottom sx={{ color: 'white', mb: 1 }}>
                    Select Date Range
                </Typography>
                <ButtonGroup 
                    variant="outlined" 
                    aria-label="date range presets" 
                    size="small" 
                    sx={{ 
                        mb: 2,
                        flexWrap: 'wrap',
                        '& .MuiButton-root': {
                            margin: '2px',
                        }
                    }}
                >
                    {presets.map((preset) => (
                        <Button
                            key={preset.id}
                            onClick={() => handlePresetClick(preset.id)}
                            variant={activePreset === preset.id ? 'contained' : 'outlined'}
                            size="small"
                        >
                            {preset.label}
                        </Button>
                    ))}
                </ButtonGroup>
                
                {showCustomDatePicker && (
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <DatePicker
                                    label="Start Date"
                                    value={startDate}
                                    onChange={(date) => handleCustomDateChange('start', date)}
                                    slotProps={{ 
                                        textField: { 
                                            fullWidth: true,
                                            size: "small"
                                        } 
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <DatePicker
                                    label="End Date"
                                    value={endDate}
                                    onChange={(date) => handleCustomDateChange('end', date)}
                                    slotProps={{ 
                                        textField: { 
                                            fullWidth: true,
                                            size: "small"
                                        } 
                                    }}
                                    minDate={startDate}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                )}
            </Popover>
        </Box>
    );
};

export default DateFilter; 